/**
 * Hook for adding USDC liquidity to a market (operator only)
 */
import { addLiquidity, getMarketBalance, USDC_DECIMALS } from "@breakout/sdk"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import type { Address } from "viem"
import { erc20Abi, formatUnits, parseUnits } from "viem"
import { useAccount, useWalletClient } from "wagmi"
import { useAdminConfig } from "../lib/providers"

interface UseAddLiquidityArgs {
	marketAddress: Address
}

export function useAddLiquidity({ marketAddress }: UseAddLiquidityArgs) {
	const { data: walletClient } = useWalletClient()
	const { address } = useAccount()
	const { publicClient, chainConfig } = useAdminConfig()
	const queryClient = useQueryClient()

	const usdcAddress = chainConfig.usdc

	// Query current market balance
	const marketBalanceQuery = useQuery({
		queryKey: ["market-balance", marketAddress],
		queryFn: async () => {
			return getMarketBalance(publicClient, { market: marketAddress })
		},
		staleTime: 30_000,
	})

	// Query user's USDC balance
	const userBalanceQuery = useQuery({
		queryKey: ["usdc-balance", address],
		queryFn: async () => {
			if (!address) {
				return null
			}
			const balance = await publicClient.readContract({
				address: usdcAddress,
				abi: erc20Abi,
				functionName: "balanceOf",
				args: [address],
			})
			return {
				raw: balance,
				formatted: Number(formatUnits(balance, USDC_DECIMALS)),
			}
		},
		enabled: !!address,
		staleTime: 30_000,
	})

	// Query current allowance
	const allowanceQuery = useQuery({
		queryKey: ["usdc-allowance", address, marketAddress],
		queryFn: async () => {
			if (!address) {
				return 0n
			}
			const allowance = await publicClient.readContract({
				address: usdcAddress,
				abi: erc20Abi,
				functionName: "allowance",
				args: [address, marketAddress],
			})
			return allowance
		},
		enabled: !!address,
		staleTime: 30_000,
	})

	// Approve USDC mutation
	const approveMutation = useMutation({
		mutationFn: async ({ amount }: { amount: number }) => {
			if (!walletClient || !address) {
				throw new Error("Wallet not connected")
			}
			const amountRaw = parseUnits(amount.toString(), USDC_DECIMALS)
			const hash = await walletClient.writeContract({
				address: usdcAddress,
				abi: erc20Abi,
				functionName: "approve",
				args: [marketAddress, amountRaw],
				chain: walletClient.chain,
				account: address,
			})
			// Wait for confirmation
			await publicClient.waitForTransactionReceipt({ hash })
			return hash
		},
		onSuccess: () => {
			// Invalidate allowance query
			queryClient.invalidateQueries({ queryKey: ["usdc-allowance", address, marketAddress] })
		},
	})

	// Add liquidity mutation
	const addLiquidityMutation = useMutation({
		mutationFn: async ({ amount }: { amount: number }) => {
			if (!walletClient) {
				throw new Error("Wallet not connected")
			}
			const hash = await addLiquidity(walletClient, {
				market: marketAddress,
				amount,
			})
			// Wait for confirmation
			await publicClient.waitForTransactionReceipt({ hash })
			return hash
		},
		onSuccess: () => {
			// Invalidate market balance and user balance queries
			queryClient.invalidateQueries({ queryKey: ["market-balance", marketAddress] })
			queryClient.invalidateQueries({ queryKey: ["usdc-balance", address] })
			queryClient.invalidateQueries({ queryKey: ["usdc-allowance", address, marketAddress] })
		},
	})

	// Check if amount needs approval
	const needsApproval = (amount: number): boolean => {
		const amountRaw = parseUnits(amount.toString(), USDC_DECIMALS)
		const currentAllowance = allowanceQuery.data ?? 0n
		return amountRaw > currentAllowance
	}

	// Check if user has sufficient balance
	const hasSufficientBalance = (amount: number): boolean => {
		const userBalance = userBalanceQuery.data?.formatted ?? 0
		return userBalance >= amount
	}

	return {
		marketBalance: marketBalanceQuery.data,
		isLoadingMarketBalance: marketBalanceQuery.isLoading,
		userBalance: userBalanceQuery.data,
		isLoadingUserBalance: userBalanceQuery.isLoading,
		allowance: allowanceQuery.data,
		isLoadingAllowance: allowanceQuery.isLoading,
		needsApproval,
		hasSufficientBalance,
		approve: approveMutation.mutateAsync,
		isApproving: approveMutation.isPending,
		addLiquidity: addLiquidityMutation.mutateAsync,
		isAddingLiquidity: addLiquidityMutation.isPending,
		refetchMarketBalance: marketBalanceQuery.refetch,
		refetchUserBalance: userBalanceQuery.refetch,
	}
}
